import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';
import { RegisterSectionCard } from './RegisterSectionCard';
import { TrialSummaryCard } from './TrialSummaryCard';
import { REGISTER_LABEL_SX, REGISTER_VALUE_SX, formatRegisterAmount } from './registerSurfaces';

const { semantic } = colorTokens;

function DetailRow({ label, value, testId }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
      <Typography sx={REGISTER_LABEL_SX}>{label}</Typography>
      <Typography sx={{ ...REGISTER_VALUE_SX, textAlign: 'right' }} data-testid={testId}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Step 4 — review, ready checklist and the single confirmation gate that
 * unlocks the one and only POST /api/public/register/trial.
 */
export function RegisterStepReview({
  preview,
  previewLoading,
  previewError,
  selectedModules,
  billingInterval,
  usersCount,
  smsNotificationsEnabled,
  startMode,
  form,
  slugPreview,
  checklist,
  confirmationAccepted,
  onConfirmationChange,
  confirmationError,
  onEditModules,
  onEditCompany,
  onEditTenant,
}) {
  const { t } = useTranslation(['onboarding', 'auth', 'pricing', 'register', 'common']);

  const editLabel = t('onboarding:register.wizard.edit');
  const currency = preview?.currency || 'EUR';
  const lineItems = preview?.line_items || [];
  const moduleCodes = preview?.selected_module_codes?.length
    ? preview.selected_module_codes
    : selectedModules;

  return (
    <Stack spacing={2} data-testid="register-step-review">
      <RegisterSectionCard
        title={t('onboarding:register.wizard.review_modules')}
        actionLabel={editLabel}
        onAction={onEditModules}
        testId="register-review-modules"
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {moduleCodes.map((code) => (
            <Chip
              key={code}
              label={code}
              size="small"
              data-testid={`register-review-module-${code}`}
              sx={{
                borderRadius: `${radiusTokens.chip}px`,
                bgcolor: semantic.status.brand.bg,
                color: semantic.status.brand.text,
                border: `1px solid ${semantic.status.brand.border}`,
              }}
            />
          ))}
        </Stack>
        {lineItems.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('pricing:register.line_module')}</TableCell>
                  <TableCell>{t('pricing:register.line_monthly')}</TableCell>
                  <TableCell>{t('pricing:register.line_yearly')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item) => (
                  <TableRow key={item.canonical_code || item.public_code || item.name}>
                    <TableCell>{item.name || item.canonical_code}</TableCell>
                    <TableCell>
                      {formatRegisterAmount(item.monthly_amount, item.currency || currency, t)}
                    </TableCell>
                    <TableCell>
                      {formatRegisterAmount(item.yearly_amount, item.currency || currency, t)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </RegisterSectionCard>

      <RegisterSectionCard
        title={t('onboarding:register.wizard.review_settings')}
        actionLabel={editLabel}
        onAction={onEditModules}
        testId="register-review-trial-settings"
      >
        <Stack spacing={1}>
          <DetailRow
            label={t('pricing:pricing_preview.billing_interval')}
            value={billingInterval === 'yearly'
              ? t('pricing:pricing_preview.interval_yearly')
              : t('pricing:pricing_preview.interval_monthly')}
            testId="register-review-billing"
          />
          <DetailRow
            label={t('pricing:register.users_count')}
            value={preview?.user_tier_label || usersCount}
            testId="register-review-users"
          />
          <DetailRow
            label={t('register:smsNotifications')}
            value={smsNotificationsEnabled ? t('register:smsUsageBased') : t('common:no')}
            testId="register-review-sms"
          />
          <DetailRow
            label={t('register:startMode')}
            value={startMode === 'buy_now' ? t('register:buyNow') : t('register:startTrial')}
            testId="register-review-start-mode"
          />
        </Stack>
      </RegisterSectionCard>

      <RegisterSectionCard
        title={t('onboarding:register.wizard.review_company')}
        actionLabel={editLabel}
        onAction={onEditCompany}
        testId="register-review-company"
      >
        <Stack spacing={1}>
          <DetailRow
            label={t('onboarding:register.wizard.customer_type_label')}
            value={form.customer_type
              ? t('onboarding:register.wizard.customer_type_' + form.customer_type)
              : t('common:empty_value')}
            testId="register-review-customer-type"
          />
          {form.customer_type === 'business' || form.company_name ? (
            <DetailRow label={t('auth:company_name')} value={form.company_name} />
          ) : null}
          <DetailRow label={t('auth:contact_name')} value={form.contact_name} />
          <DetailRow label={t('auth:contact_email')} value={form.contact_email} />
          {form.phone_national_number ? (
            <DetailRow
              label={t('register:phoneNumber')}
              value={`${form.phone_country_code} ${form.phone_national_number}`}
            />
          ) : null}
          {form.country_name || form.country_code ? (
            <DetailRow
              label={t('register:country')}
              value={form.country_name || form.country_code}
            />
          ) : null}
          {form.address_line1 ? (
            <DetailRow
              label={t('onboarding:register.wizard.address_line1')}
              value={[form.address_line1, [form.postal_code, form.city].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(', ')}
            />
          ) : null}
        </Stack>
      </RegisterSectionCard>

      <RegisterSectionCard
        title={t('onboarding:register.wizard.review_tenant')}
        actionLabel={editLabel}
        onAction={onEditTenant}
        testId="register-review-tenant"
      >
        <Stack spacing={1}>
          <DetailRow
            label={t('auth:tenant_name')}
            value={form.tenant_name || slugPreview?.tenant_name || t('common:empty_value')}
          />
          <DetailRow
            label={t('auth:tenant_slug')}
            value={form.tenant_slug || slugPreview?.slug || t('common:empty_value')}
            testId="register-review-tenant-slug"
          />
          <DetailRow
            label={t('auth:primary_domain')}
            value={form.primary_domain || slugPreview?.primary_domain || t('common:empty_value')}
          />
        </Stack>
      </RegisterSectionCard>

      <TrialSummaryCard
        preview={preview}
        loading={previewLoading}
        error={previewError}
        selectedModules={selectedModules}
        billingInterval={billingInterval}
        usersCount={usersCount}
        smsNotificationsEnabled={smsNotificationsEnabled}
        startMode={startMode}
        variant="block"
        testId="register-final-summary"
      />

      <RegisterSectionCard
        title={t('onboarding:register.wizard.ready_checklist_title')}
        helper={t('onboarding:register.wizard.ready_checklist_helper')}
        testId="register-ready-checklist"
      >
        <Stack spacing={1}>
          {checklist.map((item) => (
            <Stack
              key={item.id}
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid={`register-checklist-${item.id}`}
              data-state={item.done ? 'done' : 'pending'}
            >
              {item.done ? (
                <CheckCircleOutlineIcon fontSize="small" sx={{ color: semantic.success }} />
              ) : (
                <RadioButtonUncheckedIcon fontSize="small" sx={{ color: semantic.text.muted }} />
              )}
              <Typography
                sx={{
                  fontSize: 13,
                  color: item.done ? semantic.text.primary : semantic.text.muted,
                }}
              >
                {t(`onboarding:register.wizard.checklist_${item.id}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </RegisterSectionCard>

      <Box
        sx={{
          p: 2,
          borderRadius: `${radiusTokens.md}px`,
          border: `1px solid ${semantic.status.brand.border}`,
          bgcolor: semantic.status.brand.bg,
        }}
        data-testid="register-confirmation"
      >
        <FormControlLabel
          sx={{ alignItems: 'flex-start', m: 0, gap: 1 }}
          control={(
            <Checkbox
              checked={confirmationAccepted}
              onChange={onConfirmationChange}
              sx={{ p: 0.5 }}
              inputProps={{ 'data-testid': 'register-confirm-input' }}
            />
          )}
          label={(
            <Typography
              sx={{
                fontFamily: typographyTokens.fontFamily,
                fontSize: 13,
                color: semantic.text.primary,
              }}
            >
              {t('onboarding:register.wizard.confirm_label')}
            </Typography>
          )}
        />
        {confirmationError ? (
          <Typography sx={{ fontSize: 12, color: semantic.error, mt: 0.5 }}>
            {t('onboarding:register.wizard.confirm_required')}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

export default RegisterStepReview;
